import React, { useState, useEffect, useRef } from "react";
import { Grid, Skeleton, Checkbox, Tooltip, Chip } from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import ButtonComponent from "../../Components/Common/button/Button";
import DataTable from "../../Components/Common/datatable/DataTable";
import ApiService from "../../utils/fetch";
import { MessagePopup } from "../../Components/Common/messagePopup/MessagePopup";
import BlackWhiteSwitch from "./BlackWhiteSwitch";

const BottlerForecastTab = ({ data, email, isEmailValid }) => {
  const gridRef = useRef(null);

  const [bottlerOption, setBottlerOption] = useState([]);
  const [partnerOption, setPartnerOption] = useState([]);
  const [plantOption, setPlantOption] = useState([]);

  const [selectedBottlers, setSelectedBottlers] = useState([]);
  const [selectedPartners, setSelectedPartners] = useState([]);
  const [selectedPlants, setSelectedPlants] = useState([]);

  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRows, setSelectedRows] = useState([]);
  const [actionInFlight, setActionInFlight] = useState(false);

  const [searchText, setSearchText] = useState("");
  const [searchPartnerText, setSearchPartnerText] = useState("");

  const selectAllPartnerOption = {
    partnerDetails: "All Forecast Partner",
    isSelectAll: true,
  };
  const selectAllOption = {
    locationDetails: "All Plants",
    isSelectAll: true,
  };

  const optionsWithSelectAllPartners =
    partnerOption.length > 0 ? [selectAllPartnerOption, ...partnerOption] : [];

  const optionsWithSelectAll =
    plantOption.length > 0 ? [selectAllOption, ...plantOption] : [];

  const [plantNoOptionsText, setPlantNoOptionsText] = useState(
    "Select Forecast Partner to view Plants",
  );

  useEffect(() => {
    fetchBCLUserAccessList();
  }, [data?.id]);

  //Table Data
  const fetchBCLUserAccessList = async () => {
    if (!data?.id) return;
    try {
      setLoading(true);
      const response = await ApiService.get(
        `/v1/admin/userManagement/fetch-user-access-list?user_details_id=${data.id}`,
      );
      setFilteredData(response.data);
    } catch (error) {
      console.error("Error fetching user details:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (data.roles.includes("Bottler")) {
      fetchBottlerSelection();
    } else {
      fetchAllUserBottlerSelection();
    }
  }, []);

  // Bottler Role Bottler Options
  const fetchBottlerSelection = async () => {
    try {
      const response = await ApiService.get(
        `/v1/admin/userManagement/bottler-by-userid?userId=${data.id}`,
      );
      if (response.data && response.data.length > 0) {
        setBottlerOption(response.data); // Set bottler options if data is not empty
      }
    } catch (error) {
      console.error("Error fetching bottler selection:", error);
    }
  };

  // Non Bottler Role Bottler Options
  const fetchAllUserBottlerSelection = async () => {
    try {
      const response = await ApiService.get(
        `/v1/admin/userManagement/bottler-active-list`,
      );
      setBottlerOption(response.data);
    } catch (error) {
      console.error("Error fetching user details:", error);
    }
  };

  // Customer Options
  const updatePartnerOption = async (bottler) => {
    try {
      const response = await ApiService.get(
        `v1/admin/userManagement/customer-selection-by-bottler?bottler=${encodeURIComponent(bottler)}`,
      );

      setPartnerOption(response.data);
    } catch (error) {
      console.error("Error fetching partner and plant options:", error);
    }
  };

  // Plant Options
  const updatePlantOption = async (partners) => {
    const customerId = partners.map((item) => item.split("-")[0]);

    const payloadData = {
      userId: data.id,
      customerIds: customerId,
    };

    try {
      const response = await ApiService.post(
        `v1/admin/userManagement/location-selection-by-customer`,
        payloadData,
      );
      if (response.data && response.data.length > 0) {
        const updatedPlants = response.data.map((plant) => ({
          ...plant,
          locationDetails: `${plant.locationDetails}(${plant.customerDetails})`,
        }));
        setPlantOption(updatedPlants);
        setPlantNoOptionsText("Select Forecast Partner to view Plants"); // Reset to default message
      } else {
        setPlantOption([]); // Clear plant options
        setPlantNoOptionsText("No Plants for Select Forecast Partner"); // Update message for empty array
      }
    } catch (error) {
      console.error("Error fetching plant options:", error);
      setPlantOption([]); // Clear plant options in case of error
      setPlantNoOptionsText("Error fetching plant options"); // Optional error message
    }
  };

  //Bottler handle
  const handleBottlerChange = (event, newValue) => {
    setSelectedBottlers(newValue || []);

    // Clear dependent selections and options
    setSelectedPartners([]);
    setSelectedPlants([]);

    // Fetch partner options if any new bottler is selected
    if (newValue && newValue.length > 0) {
      updatePartnerOption(newValue);
    } else {
      // Clear options if no bottlers are selected
      setPartnerOption([]);
      setPlantOption([]);
    }
  };

  //Partner handle
  const handlePartnerChange = (event, newValue) => {
    const isSelectAllClicked = newValue.some((item) => item.isSelectAll);

    if (isSelectAllClicked) {
      if (selectedPartners.length === partnerOption.length) {
        setSelectedPartners([]);
        setPlantOption([]); // Clear plant options when unselecting all partners
      } else {
        setSelectedPartners(partnerOption);
        updatePlantOption(partnerOption); // Update plant options for all partners
      }
    } else {
      setSelectedPartners(newValue || []);
      setSelectedPlants([]); // Clear dependent plants selection

      // Fetch plant options if any new partner is selected
      if (newValue && newValue.length > 0) {
        updatePlantOption(newValue);
      } else {
        setPlantOption([]); // Clear plant options if no partners are selected
      }
    }
  };

  // Enable
  const handleEnable = async () => {
    if (actionInFlight) return;

    if (email === "") {
      MessagePopup({
        icon: "error",
        title: "Email Required",
        message: "Please enter a Email address before proceeding.",
      });
      return;
    }

    setActionInFlight(true);
    if (
      !selectedBottlers ||
      selectedBottlers.length === 0 ||
      !selectedPartners ||
      selectedPartners.length === 0 ||
      !selectedPlants ||
      selectedPlants.length === 0
    ) {
      MessagePopup({
        icon: "error",
        title: "Selection Failed",
        message:
          "To proceed, please choose a Bottler, Forecast Partner, and Plant.",
      });
      return;
    }

    // Group selectedPlants by customerDetails
    const customerMap = {};
    selectedPlants.forEach((plant) => {
      const customerID = plant.customerDetails;
      const locationID = plant.locationDetails.split("-")[0]; // Assuming location ID is before the first dash
      if (!customerMap[customerID]) {
        customerMap[customerID] = [];
      }
      customerMap[customerID].push(locationID);
    });

    // Build customers array
    const customers = Object.entries(customerMap).map(
      ([customerID, locationIds]) => ({
        customerID,
        locationIds,
      }),
    );

    // Build bottlerCustomers array (supporting multiple bottlers if needed)
    const bottlerCustomers = [
      {
        bottlerName: selectedBottlers,
        customers,
      },
    ];

    const result = await MessagePopup({
      title: "Confirm User Enable",
      html: `<p>Are you sure you want to Enable the user ? </p>`,
      showCancelButton: true,
      confirmButtonText: "Ok",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#000000",
      cancelButtonColor: "#808080",
    });

    if (!result.isConfirmed) {
      setActionInFlight(false);
      return;
    }

    const requestBody = {
      userId: data.id,
      role: data.roles,
      bottlerCustomers,
      action: "ADD",
    };

    try {
      const response = await ApiService.post(
        `/v1/admin/userManagement/manage-user`,
        requestBody,
      );

      MessagePopup({
        icon: "success",
        title: "Success",
        message: response.data,
      });

      setSelectedBottlers([]);
      setSelectedPartners([]);
      setSelectedPlants([]);
      setPartnerOption([]);
      setPlantOption([]);
      setSearchText("");
      setSearchPartnerText("");
      fetchBCLUserAccessList();

      if (data.roles.includes("Bottler")) {
        fetchBottlerSelection();
      }
    } catch (error) {
      console.error("Error adding role:", error);
      MessagePopup({
        icon: "error",
        title: "Error",
        message: "Failed to add user role. Please try again.",
      });
    } finally {
      setActionInFlight(false);
    }
  };

  //Disable
  const handleDisable = async () => {
    if (actionInFlight) return;

    if (selectedRows.length === 0) {
      MessagePopup({
        icon: "error",
        title: "No Selection",
        message: "Please select at least one row to delete.",
      });
      return;
    }

    setActionInFlight(true);

    // Group selectedRows by bottler and customerID
    const bottlerMap = {};
    selectedRows.forEach((row) => {
      if (!bottlerMap[row.bottler]) {
        bottlerMap[row.bottler] = {};
      }
      if (!bottlerMap[row.bottler][row.customerId]) {
        bottlerMap[row.bottler][row.customerId] = new Set();
      }
      bottlerMap[row.bottler][row.customerId].add(row.locationId);
    });

    // Build the request object
    const bottlerCustomers = Object.entries(bottlerMap).map(
      ([bottlerName, customersObj]) => ({
        bottlerName,
        customers: Object.entries(customersObj).map(
          ([customerID, locationIdsSet]) => ({
            customerID,
            locationIds: Array.from(locationIdsSet),
          }),
        ),
      }),
    );

    const requestBody = {
      userId: data.id,
      role: data.roles,
      bottlerCustomers,
      action: "Delete",
    };

    // Confirmation popup
    const result = await MessagePopup({
      title: "Confirm User Disable",
      html: `<p>Are you sure you want to disable the selected user access?</p>`,
      showCancelButton: true,
      confirmButtonText: "Ok",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#000000",
      cancelButtonColor: "#808080",
    });

    if (!result.isConfirmed) {
      setActionInFlight(false);
      return;
    }

    try {
      const response = await ApiService.post(
        `/v1/admin/userManagement/manage-user`,
        requestBody,
      );

      MessagePopup({
        icon: "success",
        title: "Success",
        message: response.data,
      });

      setSelectedBottlers([]);
      setSelectedPartners([]);
      setSelectedPlants([]);
      setPartnerOption([]);
      setPlantOption([]);
      setSelectedRows([]);

      fetchBCLUserAccessList();
      if (data.roles.includes("Bottler")) {
        fetchBottlerSelection();
      }
    } catch (error) {
      console.error("Error removing role:", error);
      const errorMessage =
        error.response?.data || "Failed to Disable user. Please try again.";
      // const errorMessage = "Failed to Disable user. Please try again.";
      MessagePopup({
        icon: "error",
        title: "Error",
        message: errorMessage,
      });
    } finally {
      setActionInFlight(false);
    }
  };

  const columnDefinition = [
    {
      headerName: "",
      checkboxSelection: true,
      width: 40,
      minWidth: 40,
      pinned: "left",
      suppressMenu: true,
      suppressMovable: true,
      headerCheckboxSelection: true,
    },
    {
      field: "bottler",
      headerName: "Bottler",
      sortable: true,
      unSortIcon: true,
      width: 150,
      minWidth: 150,
      filter: "agMultiColumnFilter",
      filterParams: {
        filters: [
          {
            filter: "agTextColumnFilter",
          },
          {
            filter: "agSetColumnFilter",
          },
        ],
      },
    },
    {
      field: "customeName",
      headerName: "Forecast Partner",
      sortable: true,
      unSortIcon: true,
      width: 370,
      minWidth: 370,
      valueGetter: (params) => {
        const { customerId, customeName, customerCountry } = params.data;
        return `${customerId || ""} - ${customeName || ""} - ${customerCountry || ""
          }`;
      },
      cellRenderer: (params) => {
        const display = params.value;
        return (
          <Tooltip title={<span>{display}</span>} arrow>
            <div>{display}</div>
          </Tooltip>
        );
      },
      filter: "agMultiColumnFilter",
      filterParams: {
        filters: [
          {
            filter: "agTextColumnFilter",
          },
          {
            filter: "agSetColumnFilter",
          },
        ],
      },
    },
    {
      field: "locationName",
      headerName: "Plant",
      sortable: true,
      unSortIcon: true,
      width: 370,
      minWidth: 370,
      valueGetter: (params) => {
        console.log(params.data);
        const { locationId, location, locationCountry } = params.data;
        return `${locationId || ""} - ${location || ""} - ${locationCountry || ""
          }`;
      },
      cellRenderer: (params) => {
        const display = params.value;
        return (
          <Tooltip title={<span>{display}</span>} arrow>
            <div>{display}</div>
          </Tooltip>
        );
      },
      filter: "agMultiColumnFilter",
      filterParams: {
        filters: [
          {
            filter: "agTextColumnFilter",
          },
          {
            filter: "agSetColumnFilter",
          },
        ],
      },
    },
    {
      field: "isActive",
      headerName: "Notification",
      width: 160,
      minWidth: 160,
      sortable: false,
      filter: false,
      headerComponent: (props) => (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span>Notification</span>
          <input
            type="checkbox"
            style={{
              width: 20,
              height: 20,
              accentColor: "lightgrey", // changes the checkmark color
              backgroundColor: "white",
            }}
            // Add checked state based on all items being active
            checked={filteredData.every((item) => item.isActive)}
            onChange={async (event) => {
              const isChecked = event.target.checked;

              const customerInfoMap = {};
              filteredData.forEach((item) => {
                if (!customerInfoMap[item.customerId]) {
                  customerInfoMap[item.customerId] = [];
                }
                customerInfoMap[item.customerId].push(item.locationId);
              });

              const customerInfoList = Object.entries(customerInfoMap).map(
                ([customerID, locationIds]) => ({
                  customerID,
                  locationIds,
                }),
              );

              try {
                await ApiService.post(
                  "v1/admin/userManagement/update-bulk-mail-access",
                  {
                    userId: data.id,
                    isActive: isChecked,
                    customerInfoList,
                  },
                );

                fetchBCLUserAccessList();
                MessagePopup({
                  icon: "success",
                  title: "Success",
                  message: `All notifications turned ${isChecked ? "ON" : "OFF"
                    } successfully.`,
                });
              } catch (error) {
                console.error("Bulk update error:", error);
                const errorMessage =
                  error.response?.data || "Failed to update all notifications.";

                MessagePopup({
                  icon: "error",
                  title: "Error",
                  message: errorMessage,
                });
              }
            }}
          />
        </div>
      ),

      cellRenderer: (params) => (
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <span style={{ fontSize: 13, marginRight: 4 }}>OFF</span>
          <BlackWhiteSwitch
            checked={params.data.isActive}
            onChange={async (event) => {
              const isChecked = event.target.checked;

              try {
                await ApiService.post(
                  "/v1/admin/userManagement/update-mail-access",
                  {
                    userId: data.id,
                    customerId: params.data.customerId,
                    locationId: params.data.locationId,
                    isActive: isChecked,
                  },
                );
                params.node.setDataValue("isActive", isChecked);
              } catch (error) {
                console.error("Error updating notification:", error);
                const errorMessage =
                  error.response?.data ||
                  "Failed to update notification. Please try again.";

                MessagePopup({
                  icon: "error",
                  title: "Error",
                  message: errorMessage,
                });
              }
            }}
          />
          <span style={{ fontSize: 13, marginLeft: 4 }}>ON</span>
        </div>
      ),
    },
  ];

  const pagination = true;
  const paginationPageSize = 10;
  const paginationPageSizeSelector = [5, 10, 20, 50, 100];

  const onGrid = (params) => {
    const gridApi = params.api;
    if (gridApi) {
      gridApi.sizeColumnsToFit();

      // Adding a resize event listener to handle zoom changes
      window.addEventListener("resize", () => {
        gridApi.sizeColumnsToFit();
        gridApi.refreshCells();
      });
    } else {
      console.error("Grid API is not available.");
    }
  };

  const onSelectionChanged = (event) => {
    const selected = event.api.getSelectedRows();
    setSelectedRows(selected);
  };

  useEffect(() => {
    if (gridRef.current && gridRef.current.api && filteredData.length > 0) {
      gridRef.current.api.sizeColumnsToFit();
    }
  }, [filteredData]);

  return (
    <>
      <Grid
        container
        spacing={0.5}
        alignItems="center"
        marginTop={2}
        marginBottom={2}
      >
        <Grid item xs={12} sm={2}>
          <Autocomplete
            options={bottlerOption}
            getOptionLabel={(option) => option}
            value={selectedBottlers}
            onChange={handleBottlerChange}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Bottler *"
                variant="outlined"
                size="small"
                InputLabelProps={{
                  shrink: true,
                  style: {
                    transform: "translate(14px, -14px) scale(0.75)",
                  },
                }}
              />
            )}
            sx={{ width: "100%" }}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <Autocomplete
            multiple
            limitTags={1}
            id="partner-autocomplete"
            options={optionsWithSelectAllPartners}
            getOptionLabel={(option) =>
              option.isSelectAll ? option.partnerDetails : option
            }
            value={selectedPartners}
            onChange={(event, newValue, reason) => {
              const isSelectAllClicked = newValue.some(
                (item) => item.isSelectAll,
              );
              if (isSelectAllClicked) {
                if (selectedPartners.length === partnerOption.length) {
                  setSelectedPartners([]);
                } else {
                  setSelectedPartners(partnerOption);
                }
                setSearchPartnerText(""); // Clear search text after Select All action
              } else {
                setSelectedPartners(newValue);
              }
              handlePartnerChange(event, newValue, reason); // Preserve original handler
            }}
            onInputChange={(event, newInputValue, reason) => {
              // Update search text only when typing or clearing the input
              if (reason === "input" || reason === "clear") {
                setSearchPartnerText(newInputValue);
              }
            }}
            inputValue={searchPartnerText} // Control the input value
            disableCloseOnSelect
            noOptionsText="Select Bottler to view Forecast Partner"
            renderOption={(props, option, { selected }) => (
              <li {...props}>
                <Checkbox
                  style={{ marginRight: 8 }}
                  checked={
                    option.isSelectAll
                      ? selectedPartners.length === partnerOption.length
                      : selected
                  }
                />
                {option.isSelectAll ? option.partnerDetails : option}
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Forecast Partner *"
                variant="outlined"
                size="small"
                InputLabelProps={{
                  shrink: true,
                  style: {
                    transform: "translate(14px, -14px) scale(0.75)",
                  },
                }}
                sx={{
                  "& .MuiInputBase-root": {
                    maxHeight: 110,
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "center",
                    flexDirection: "row",
                    overflow: "auto",
                  },
                  "& .MuiAutocomplete-input": {
                    order: -1, // Input appears before chips
                    minWidth: "100px", // Ensure input stays visible
                    flexGrow: 1,
                    position: "relative", // Input takes available space
                  },
                  "& .MuiChip-root": {
                    order: 0, // Chips appear after input
                  },
                }}
              />
            )}
            renderTags={(selected, getTagProps) =>
              selected.map((option, index) => (
                <Chip
                  key={option.isSelectAll ? option.partnerDetails : option}
                  label={option.isSelectAll ? option.partnerDetails : option}
                  {...getTagProps({ index })}
                />
              ))
            }
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <Autocomplete
            multiple
            limitTags={1}
            id="plant-autocomplete"
            options={optionsWithSelectAll}
            getOptionLabel={(option) => option.locationDetails}
            value={selectedPlants}
            onChange={(event, newValue, reason) => {
              const isSelectAllClicked = newValue.some(
                (item) => item.isSelectAll,
              );
              if (isSelectAllClicked) {
                if (selectedPlants.length === plantOption.length) {
                  setSelectedPlants([]);
                } else {
                  setSelectedPlants(plantOption);
                }
                setSearchText(""); // Clear search text after Select All action
              } else {
                setSelectedPlants(newValue);
              }
            }}
            onInputChange={(event, newInputValue, reason) => {
              // Update search text only when typing or clearing the input
              if (reason === "input" || reason === "clear") {
                setSearchText(newInputValue);
              }
            }}
            inputValue={searchText} // Control the input value
            disableCloseOnSelect
            filterSelectedOptions={false}
            noOptionsText={plantNoOptionsText}
            renderOption={(props, option, { selected }) => (
              <li {...props}>
                <Checkbox
                  style={{ marginRight: 8 }}
                  checked={
                    option.isSelectAll
                      ? selectedPlants.length === plantOption.length
                      : selected
                  }
                />
                {option.locationDetails}
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Plant *"
                variant="outlined"
                size="small"
                InputLabelProps={{
                  shrink: true,
                  style: {
                    transform: "translate(14px, -14px) scale(0.75)",
                  },
                }}
                sx={{
                  "& .MuiInputBase-root": {
                    maxHeight: 110,
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "center",
                    flexDirection: "row",
                    overflow: "auto",
                  },
                  "& .MuiAutocomplete-input": {
                    order: -1, // Input appears before chips
                    minWidth: "100px", // Ensure input stays visible
                    flexGrow: 1,
                    position: "relative", // Input takes available space
                  },
                  "& .MuiChip-root": {
                    order: 0, // Chips appear after input
                  },
                }}
              />
            )}
            renderTags={(selected, getTagProps) =>
              selected.map((option, index) => (
                <Chip
                  key={option.locationDetails}
                  label={option.locationDetails}
                  {...getTagProps({ index })}
                />
              ))
            }
          />
        </Grid>
        <Grid item xs={12} sm={1} display="flex" justifyContent="flex-end">
          <ButtonComponent
            bgColor="black"
            color="white"
            label="Enable"
            fullWidth
            onClick={handleEnable}
            disabled={actionInFlight}
          />
        </Grid>
        <Grid item xs={12} sm={1} display="flex" justifyContent="flex-end">
          <ButtonComponent
            bgColor="black"
            color="white"
            label="Disable"
            fullWidth
            onClick={handleDisable}
            disabled={actionInFlight}
          />
        </Grid>
      </Grid>
      {loading ? (
        <Skeleton
          variant="rectangular"
          height={"68vh"}
          animation="pulse"
          style={{ marginBottom: "16px", borderRadius: "8px" }}
        />
      ) : (
        <DataTable
          ref={gridRef}
          columns={columnDefinition}
          rows={filteredData || []}
          tableHeight={"68vh"}
          suppressMovableColumns={true}
          suppressHorizontalScroll={false}
          suppressPaginationPanel={false}
          pagination={pagination}
          paginationPageSize={paginationPageSize}
          paginationPageSizeSelector={paginationPageSizeSelector}
          onGridReady={onGrid}
          rowSelection="multiple"
          onSelectionChanged={onSelectionChanged}
        />
      )}
    </>
  );
};

export default BottlerForecastTab;
